import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as PlusIcon } from '@elementary/common-ui/assets/20x20/plus-black.svg';
import { ReactComponent as TrashIcon } from '@elementary/common-ui/assets/trash.svg';
import {
  Alert,
  Button,
  Flex,
  FlexColumn,
  IconButton,
  Link,
  PasswordField,
  TextField,
  formFieldRequiredLabel as requiredLabel,
} from '@elementary/common-ui/components';
import FormControls from '@elementary/common-ui/components/FormControls';
import { Typography } from '@mui/material';
import { useFieldArray, useForm } from 'react-hook-form';
import useSupportChat from 'shared/hooks/useSupportChat';
import { isValidUrl } from 'shared/utils/validators';
import { FetchRootlyApi, RootlyIntegrationForm } from 'shared/types';
import { IntegrationError } from 'settings/types';
import { fontSize, fontWeight } from '@elementary/common-ui/style/utils';
import IntegrationDialog from '../../../shared/Integration/IntegrationDialog';
import TestIntegrationSection from '../IntegrationTestSections/TestIntegrationSection';
import RootlyTestSection from '../IntegrationTestSections/RootlyTestSection';

export const ROOTLY_SECRET_PLACEHOLDER = 'xxxxxxxxxxxxxxxxxxxxxx';

type Props = {
  open: boolean;
  onClose: () => void;
  data?: FetchRootlyApi;
  onRootlyIntegrationSubmit: (data: RootlyIntegrationForm) => void;
  error?: IntegrationError;
  loading?: boolean;
  onDisconnect?: () => void;
};

const RootlyDialog = ({
  open,
  onClose,
  data,
  onRootlyIntegrationSubmit,
  error,
  loading,
  onDisconnect,
}: Props) => {
  const [showError, setShowError] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const supportChat = useSupportChat();

  const defaultValues = useMemo(
    (): RootlyIntegrationForm => ({
      sources:
        data?.sources && data.sources.length > 0
          ? data.sources.map((s) => ({
              name: s.name,
              webhookEndpoint: s.webhookEndpoint,
              secret: ROOTLY_SECRET_PLACEHOLDER,
            }))
          : [{ name: '', webhookEndpoint: '', secret: '' }],
    }),
    [data],
  );

  const methods = useForm<RootlyIntegrationForm>({
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    control,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'sources',
  });

  useEffect(() => {
    if (error) {
      setShowError(true);
    } else {
      setShowError(false);
    }
  }, [error]);

  const resetForm = useCallback(() => {
    setShowError(false);
    reset(defaultValues);
  }, [defaultValues, reset]);

  const missingSources = useMemo<string[]>(
    () => error?.missingSources || [],
    [error],
  );

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title="Connect Rootly"
      onSubmit={handleSubmit((formData) => {
        const processedSources = formData.sources.map((source) => ({
          ...source,
          secret:
            source.secret === ROOTLY_SECRET_PLACEHOLDER ? '' : source.secret,
        }));
        onRootlyIntegrationSubmit({ sources: processedSources });
      })}
      reset={resetForm}
      error={error}
      loading={loading}
      width={972}
    >
      <FlexColumn gap={3}>
        <Typography
          sx={{ fontSize: fontSize('sm'), color: 'content.secondary' }}
        >
          Configure alert sources from your{' '}
          <Link
            href="https://docs.rootly.com/alerts/alert-sources/webhook"
            target="_blank"
            rel="noopener noreferrer"
          >
            Rootly generic webhook alert sources
          </Link>
          . Each source has its own name, webhook endpoint URL, and secret.
        </Typography>

        <FlexColumn sx={{ gap: 1 }}>
          {fields.map((field, index) => (
            <Flex gap={1} alignItems="center" key={field.id}>
              <FormControls.InputWrapper sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  placeholder="source name"
                  {...register(`sources.${index}.name`, {
                    required: requiredLabel,
                  })}
                  size="large"
                />
                <FormControls.ErrorMessage paddingLeft={0.5}>
                  {errors.sources?.[index]?.name?.message}
                </FormControls.ErrorMessage>
              </FormControls.InputWrapper>

              <FormControls.InputWrapper sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  placeholder="webhook endpoint URL"
                  {...register(`sources.${index}.webhookEndpoint`, {
                    required: requiredLabel,
                    validate: (value) =>
                      isValidUrl(value) ? true : 'This is not a valid url',
                  })}
                  size="large"
                />
                <FormControls.ErrorMessage paddingLeft={0.5}>
                  {errors.sources?.[index]?.webhookEndpoint?.message}
                </FormControls.ErrorMessage>
              </FormControls.InputWrapper>

              <FormControls.InputWrapper sx={{ flex: 1 }}>
                <PasswordField
                  fullWidth
                  placeholder="webhook secret"
                  size="large"
                  {...register(`sources.${index}.secret`, {
                    required: requiredLabel,
                  })}
                />
                <FormControls.ErrorMessage paddingLeft={0.5}>
                  {errors.sources?.[index]?.secret?.message}
                </FormControls.ErrorMessage>
              </FormControls.InputWrapper>

              <IconButton onClick={() => remove(index)}>
                <TrashIcon />
              </IconButton>
            </Flex>
          ))}

          <Button
            startIcon={<PlusIcon />}
            onClick={() =>
              append({ name: '', webhookEndpoint: '', secret: '' })
            }
            sx={{ width: '160px' }}
            variant="text"
            color="secondary"
          >
            <Typography fontSize={14}>Add source</Typography>
          </Button>
        </FlexColumn>

        {missingSources.length > 0 && (
          <Alert severity="error">
            The following sources are configured in alert rules, but are missing
            from the configuration
            {missingSources.map((source) => (
              <li key={source}>{source}</li>
            ))}
          </Alert>
        )}

        {showError && missingSources.length === 0 && (
          <Alert severity="error" sx={{ fontWeight: fontWeight('semibold') }}>
            Elementary Rootly app failed to connect, please{' '}
            <Link onClick={supportChat.show} href="#">
              contact us
            </Link>{' '}
            for further assistance.
          </Alert>
        )}

        <TestIntegrationSection isSuccess={testSuccess}>
          <RootlyTestSection
            sources={watch('sources')}
            onSuccess={() => setTestSuccess(true)}
            onReset={() => setTestSuccess(false)}
          />
        </TestIntegrationSection>

        {data?.integrated && onDisconnect && (
          <Button
            variant="text"
            color="error"
            onClick={onDisconnect}
            sx={{ alignSelf: 'flex-start', mt: 1 }}
          >
            Disconnect Rootly
          </Button>
        )}
      </FlexColumn>
    </IntegrationDialog>
  );
};

export default RootlyDialog;
